// ============================================================
// access-lifecycle.js — the "stopped staff" (offboarding) predicate.
//
// Access-lifecycle (2026-07-15). Lives in src/platform/ because the
// stopped-staff overlay is CROSS-CUTTING — it changes how EVERY module
// and the app shell behave for a departing staff member, regardless of
// which modules the tenant has enabled (same reasoning as reporter.js).
//
// MECHANISM (decided design — auth-security + firebase-expert +
// principal-architect + data-architect synthesis):
//   A staff is "stopped" when their /users/{uid} doc carries an
//   `accessCutoffAt` Firestore Timestamp AND the wall clock has passed
//   it. Pure runtime date-comparison — NO scheduled Cloud Function, NO
//   role flip, NO account disable. Reversal = clear accessCutoffAt.
//
// This module is the SINGLE SOURCE of:
//   1. the stopped predicate  (isAccessStopped) — mirrored by the
//      Firestore Rules helper isStopped() in firestore.rules, and
//   2. the keep-perms allow-list (STOPPED_KEEP_PERMS) — the only three
//      permissions a stopped staff retains.
//
// PURE + TESTABLE: no Firestore SDK imports. Accepts a plain user-doc
// object (as returned by fetchUserDoc) and an optional now-millis. The
// only shape assumption is that accessCutoffAt, when present, is a
// Firestore Timestamp (has .toMillis()) — with defensive fallbacks for
// Date / number / {seconds}/{_seconds} serialized forms so a re-hydrated
// snapshot can't silently read as "not stopped".
// ============================================================

/**
 * The ONLY permissions a stopped staff keeps. A whitelist, not a mask —
 * _hasPerm() returns true for a stopped user ONLY if the requested perm
 * is in this set (see src/main.js). Each maps to a keep-surface:
 *   - dashboard.read     → the minimal stopped-dashboard
 *   - payroll.read.self  → My Payslips (self-read, AuthEmail/LoginUid)
 *   - account.read.self  → My Profile (self-read of own employee doc)
 *   - policies.read      → Staff Policies (2026-09-21, see below)
 *
 * Everything else (inventory, procurement, sales, finance, HR, leave,
 * settings, and all writes) is denied — cooperatively in the UI here,
 * and non-bypassably in firestore.rules.
 *
 * 2026-09-21 — `policies.read` added. A departing worker keeps the right
 * to read the company policies that still govern their notice period,
 * final pay and outstanding medical/sick-leave entitlement; a policy
 * library that locks on the way out is the moment it is most needed.
 * NOTE this is the FIRST keep-perm with no self-identity ("mine") field
 * to ride on — payslips ride AuthEmail/LoginUid, employees rides the
 * self-exception. A policy doc belongs to the tenant, not a person, so
 * the Rules side needed a real permission lookup for a stopped user:
 * hence hasPermRaw() in firestore.rules (hasPerm() minus !isStopped()).
 * A keep-perm added here MUST have a matching hasPermRaw() call site in
 * the Rules, or the tab renders and every read throws.
 *
 * NEVER add a WRITE permission here. In particular `policies.acknowledge`
 * (deferred to v1.1) must never enter this set: an acknowledgement
 * recorded after the access cutoff is not evidence of anything.
 */
export const STOPPED_KEEP_PERMS = new Set([
  'dashboard.read',
  'payroll.read.self',
  'account.read.self',
  'policies.read'
]);

/**
 * Coerce an accessCutoffAt value of any plausible shape to epoch millis.
 * Returns null when the value is absent or unparseable (→ "not stopped").
 *
 * @param {*} v  Firestore Timestamp | Date | number(ms) | {seconds}/{_seconds} | ISO string
 * @returns {number|null}
 */
function _toMillis(v) {
  if (v == null) return null;
  // Firestore Timestamp (client SDK + Admin SDK both expose toMillis()).
  if (typeof v.toMillis === 'function') {
    try { return v.toMillis(); } catch (e) { return null; }
  }
  // Firestore Timestamp re-hydrated from JSON: { seconds, nanoseconds } or
  // { _seconds, _nanoseconds }.
  if (typeof v.seconds === 'number')  return v.seconds  * 1000;
  if (typeof v._seconds === 'number') return v._seconds * 1000;
  // JS Date.
  if (v instanceof Date) {
    const t = v.getTime();
    return isNaN(t) ? null : t;
  }
  // Raw millis.
  if (typeof v === 'number') return isNaN(v) ? null : v;
  // ISO / date string.
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return isNaN(t) ? null : t;
  }
  return null;
}

/**
 * Is this user "stopped" as of `nowMs`?
 *
 * True IFF the user doc has an `accessCutoffAt` AND it is at or before
 * `nowMs`. A future accessCutoffAt (stop date scheduled but not yet
 * reached — e.g. between now and the departing staff's last day) reads
 * as NOT stopped, so they keep full access until the cutoff instant.
 *
 * @param {object|null} userDoc  the /users/{uid} doc ({ ...snap.data() })
 * @param {number}      nowMs    epoch millis to compare against (default now)
 * @returns {boolean}
 */
export function isAccessStopped(userDoc, nowMs = Date.now()) {
  if (!userDoc) return false;
  const cutoffMs = _toMillis(userDoc.accessCutoffAt);
  if (cutoffMs == null) return false;
  return cutoffMs <= nowMs;
}
